import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { appTitle, getAppName } from "../utils/appName";

function firebaseAuthMessage(err: unknown, mode: "sign-in" | "register" | "forgot-password"): string {
  const code = (err as { code?: string }).code ?? "";
  switch (code) {
    case "auth/invalid-email":
      return "That doesn't look like a valid email address.";
    case "auth/user-not-found":
      return "No account uses this email. Register instead?";
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "Email or password is incorrect.";
    case "auth/email-already-in-use":
      return "This email is already registered. Sign in instead.";
    case "auth/weak-password":
      return "Password must be at least 6 characters.";
    case "auth/account-exists-with-different-credential":
      return "An account with this email already exists. Try a different sign-in method.";
    case "auth/network-request-failed":
      return "Couldn't reach the server. Check your connection.";
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
      return "";
    default:
      return mode === "register" ? "Registration failed. Please try again." : "Sign-in failed. Please try again.";
  }
}

export default function LoginPage() {
  const { user, loading, login, loginWithEmail, registerWithEmail, resetPassword } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [mode, setMode] = useState<"sign-in" | "register" | "forgot-password">("sign-in");
  const [emailError, setEmailError] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/keys", { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    document.title = `Sign in · ${appTitle()}`;
  }, []);

  function switchMode(next: "sign-in" | "register" | "forgot-password") {
    setMode(next);
    setEmailError("");
    setResetSent(false);
  }

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    setEmailError("");
    setEmailLoading(true);
    try {
      if (mode === "sign-in") {
        await loginWithEmail(email, password);
      } else {
        await registerWithEmail(email, password);
      }
    } catch (err: unknown) {
      setEmailError(firebaseAuthMessage(err, mode));
    } finally {
      setEmailLoading(false);
    }
  }

  async function handleResetSubmit(e: React.FormEvent) {
    e.preventDefault();
    setEmailError("");
    setEmailLoading(true);
    try {
      await resetPassword(email);
    } catch {
      // Always show success — don't reveal whether an account exists
    } finally {
      setEmailLoading(false);
      setResetSent(true);
    }
  }

  return (
    <div className="flex h-screen flex-col items-center justify-center gap-8 bg-bone">
      <div className="text-center">
        <h1 className="font-mono text-4xl font-semibold text-gray-900 tracking-tight">
          <span className="text-brand-600">{"{"}</span>stringly<span className="text-brand-600">{"}"}</span>
        </h1>
        {getAppName() && (
          <p className="mt-1 text-sm text-gray-400">for {getAppName()}</p>
        )}
        <p className="mt-3 text-sm text-gray-500">
          Localization that lives in your repo, not someone else's cloud.
        </p>
      </div>

      <div className="w-full max-w-sm rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
        <h2 className="mb-6 text-center text-lg font-semibold text-gray-800">
          {mode === "sign-in" ? "Sign in" : mode === "register" ? "Create account" : "Reset password"}
        </h2>

        {/* Forgot password form */}
        {mode === "forgot-password" && (
          <>
            {resetSent ? (
              <p className="text-sm text-gray-700 text-center">
                Check your inbox — if that email is registered you'll receive a reset link shortly.
              </p>
            ) : (
              <form onSubmit={handleResetSubmit} className="flex flex-col gap-3">
                <label htmlFor="auth-email" className="sr-only">Email</label>
                <input
                  id="auth-email"
                  type="email"
                  className="input"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  aria-describedby="auth-error"
                />
                <p id="auth-error" role="alert" className="text-xs text-red-600 min-h-[1rem]">
                  {emailError}
                </p>
                <button
                  type="submit"
                  className="btn-primary w-full justify-center py-2.5 min-h-[44px]"
                  disabled={emailLoading}
                >
                  {emailLoading ? "…" : "Send reset link"}
                </button>
              </form>
            )}
            <p className="mt-4 text-center text-xs text-gray-500">
              <button type="button" className="underline text-gray-700" onClick={() => switchMode("sign-in")}>
                Back to sign in
              </button>
            </p>
          </>
        )}

        {/* Sign-in / Register form */}
        {mode !== "forgot-password" && (
          <>
            <form onSubmit={handleEmailSubmit} className="flex flex-col gap-3">
              <label htmlFor="auth-email" className="sr-only">Email</label>
              <input
                id="auth-email"
                type="email"
                className="input"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                aria-describedby="auth-error"
              />
              <label htmlFor="auth-password" className="sr-only">Password</label>
              <div className="relative">
                <input
                  id="auth-password"
                  type={showPassword ? "text" : "password"}
                  className="input w-full pr-10"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  autoComplete={mode === "sign-in" ? "current-password" : "new-password"}
                  aria-describedby="auth-error"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" focusable="false">
                      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
                      <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" focusable="false">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
              {mode === "sign-in" && (
                <div className="text-right">
                  <button
                    type="button"
                    className="text-xs text-gray-500 underline hover:text-gray-700"
                    onClick={() => switchMode("forgot-password")}
                  >
                    Forgot password?
                  </button>
                </div>
              )}
              <p id="auth-error" role="alert" className="text-xs text-red-600 min-h-[1rem]">
                {emailError}
              </p>
              <button
                type="submit"
                className="btn-primary w-full justify-center py-2.5 min-h-[44px]"
                disabled={emailLoading}
              >
                {emailLoading ? "…" : mode === "sign-in" ? "Sign in with email" : "Create account"}
              </button>
            </form>

            <p className="mt-2 text-center text-xs text-gray-500">
              {mode === "sign-in" ? (
                <>No account?{" "}
                  <button type="button" className="underline text-gray-700" onClick={() => switchMode("register")}>
                    Register
                  </button>
                </>
              ) : (
                <>Already have an account?{" "}
                  <button type="button" className="underline text-gray-700" onClick={() => switchMode("sign-in")}>
                    Sign in
                  </button>
                </>
              )}
            </p>

            <div className="my-5 flex items-center gap-3">
              <div className="flex-1 border-t border-gray-200" />
              <span className="text-xs text-gray-400">or</span>
              <div className="flex-1 border-t border-gray-200" />
            </div>

            {/* Google */}
            <button
              type="button"
              onClick={async () => { try { await login(); } catch (err) { setEmailError(firebaseAuthMessage(err, mode)); } }}
              className="btn-secondary w-full justify-center py-2.5 min-h-[44px] flex items-center gap-2"
            >
              <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </button>
          </>
        )}
      </div>
    </div>
  );
}
